<template>
  <div class="dashboard-wrapper">
    <h4 v-if="title">
      {{ title }}
    </h4>
    <ul class="product-thumbnail-list">
      <slot />
      <li
        v-for="(product, index) in getProductList"
        :key="index"
        class="dashboard-item"
        :class="{ 'consolidated-product-name-exp': configData.consolidatedProductNameExp }"
      >
        <Skeleton
          v-if="loading"
          height="150px"
          width="220px"
        />
        <ProductThumbnail
          v-else
          :product="product"
          alignment="horizontal"
          :hide-review-text-and-rating="true"
          :image-width="deviceType === 'small' ? 57 : 82"
          :display-sale-price-only="!isMcom"
          :description-alignment="!isMcom ? 'center' : 'left'"
          :brand-class="!isMcom ? '' : 'medium'"
          :open-in-new-tab="configData.openInNewTab"
          :is-simplification-on="configData.isSimplificationOn"
          :simplification-with-v1="configData.simplificationWithV1"
          :simplification-non-bold-percentage-exp="configData.simplificationNonBoldPercentageExp"
          :simplification-remove-sale-exp="configData.simplificationRemoveSaleExp"
        />
      </li>
    </ul>
  </div>
</template>

<script>
import { ProductThumbnail } from '@common-vue/product-thumbnail';
import Skeleton from '@atomic-ui/skeleton';
import fetchProducts from '../../services/fetchProducts';
import { getDeviceType, isValidProduct, isMcom } from '../../utils/utils';
import constants from '../../utils/constants';

const { LARGE } = constants.BREAK_POINT_TYPE;

export default {
  name: 'Dashboard',
  components: { ProductThumbnail, Skeleton },
  props: {
    configData: {
      type: Object,
      default: () => ({}),
    },
  },
  setup() {
    return { isMcom: isMcom() };
  },
  data() {
    return {
      testdata: null,
      observer: null,
      productsListSkeleton: [0, 1],
      productsList: [],
      loading: true,
      deviceType: LARGE,
      title: this.configData.title,
    };
  },
  computed: {
    getProductList() {
      return this.loading ? this.productsListSkeleton : this.productsList;
    },
    getProductIds() {
      return this.configData.productIds?.length > 0
        ? this.configData.productIds
        : this.configData.recommendationData?.recommendedIdsList?.map(
          (a) => a.id,
        );
    },
  },
  watch: {
    productsList(newValue, oldValue) {
      if (JSON.stringify(newValue) !== JSON.stringify(oldValue)) {
        setTimeout(() => {
          // this.loading = false;
          this.$store.commit('recommendationStore/setPanelLoadedState', {
            workflow: this.configData.workflow,
            isLoaded: true,
          });
        }, constants.SKELETON_LOADING_TIMEOUT);
      }
    },
  },
  beforeMount() {
    this.getProductsAndValidate();
  },
  mounted() {
    this.setDeviceType();
    window.addEventListener('resize', this.setDeviceType);
    window.addEventListener('orientationchange', this.setDeviceType);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.setDeviceType);
    window.removeEventListener('orientationchange', this.setDeviceType);
  },
  methods: {
    // TODO: filter out review data
    async getProductsAndValidate() {
      try {
        const products = await fetchProducts(
          this.getProductIds,
          2,
          isValidProduct,
        );
        this.productsList = products.map((product) => {
          if (product.product?.[0].detail) {
            delete product.product[0].detail.reviewStatistics;
          }
          if (product.product?.[0].pricing?.price?.priceType) {
            delete product.product[0].pricing.price.priceType.text;
          }
          return product;
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        this.productsList = [];
      } finally {
        this.loading = false;
      }
    },
    setDeviceType() {
      this.deviceType = getDeviceType();
    },
  },
};
</script>

<style lang="scss">
.dashboard-wrapper {
  position: relative;
  display: flex;
  flex-direction: column;
  row-gap: rem-calc(16);
  width: 100%;

  @include breakpoint(large up) {
    row-gap: rem-calc(24);
  }

  @media (min-width: 1480px) {
    flex-direction: row;
    column-gap: rem-calc(24);
    padding-left: rem-calc(48);
  }

  h4 {
    @media (min-width: 1480px) {
      margin-top: rem-calc(12);
    }
  }

  .product-thumbnail-list {
    list-style: none;
    display: flex;
    justify-content: space-around;
    flex-direction: column;
    width: 100%;
    gap: rem-calc(8);

    @include breakpoint(large up) {
      flex-direction: row;
      margin-top: 0;
    }

    @media (min-width: 1480px) {
      flex: 0 0 70%;
    }

    .dashboard-item {
      .product-thumbnail-container {
        &.horizontal-alignment {
          column-gap: rem-calc(12);
        }

        .product-image {
          flex-shrink: 0;
          flex-grow: 0;
        }

        .product-description {
          margin: 0;

          .medium.product-name {
            font-weight: 400;
            -webkit-line-clamp: 1;

            @include breakpoint(large up) {
              -webkit-line-clamp: 2;
            }
          }

          .pricing.margin-pricing {
            margin-top: rem-calc(4);

            @include breakpoint(large up) {
              margin-top: rem-calc(8);
            }
          }

          .price-reg {
            font-weight: 500;
          }

          .shipping-options {
            display: none;
          }
        }
      }
    }

    .consolidated-product-name-exp {
      .product-thumbnail-container {
        .product-description {
          .medium.product-name {
            -webkit-line-clamp: 1;
          }
        }
      }
  }
}
}
</style>
